import React, { useEffect, useRef } from 'react';
import { Package, ArrowRight } from 'lucide-react';
import { PRODUCT_BOX_DISCOUNTS } from '../../data/boxDiscount';

interface BoxNudgeProps {
  sku: string;
  qty: number;
  unitPrice: number;
  onSetQty: (qty: number) => void;
  onBoxCompleted?: (boxes: number, boxQty: number) => void;
}

export function BoxNudge({ sku, qty, unitPrice, onSetQty, onBoxCompleted }: BoxNudgeProps) {
  const box = PRODUCT_BOX_DISCOUNTS[sku];

  const boxQty = box?.boxQty ?? 0;
  const boxPrice = box?.boxPrice ?? 0;
  const cadBox = boxQty > 0 ? boxPrice / boxQty : 0;
  const remainder = boxQty > 0 ? qty % boxQty : 0;
  const missing = remainder === 0 ? 0 : boxQty - remainder;
  const isExactBox = qty > 0 && boxQty > 0 && remainder === 0;

  // Hooks must be before any return
  const prevQtyRef = useRef(qty);
  useEffect(() => {
    if (isExactBox && prevQtyRef.current !== qty && onBoxCompleted) {
      const boxes = qty / boxQty;
      onBoxCompleted(boxes, boxQty);
    }
    prevQtyRef.current = qty;
  }, [qty, isExactBox, boxQty, onBoxCompleted]);

  if (!box || qty === 0) return null;

  const threshold = 1;
  const isClose = missing > 0 && missing <= threshold;

  if (isExactBox) return null;

  if (isClose) {
    const nextBoxQty = (Math.floor(qty / boxQty) + 1) * boxQty;

    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-2" data-testid="box-nudge-close">
        <div className="flex items-start gap-2">
          <Package className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[10px]">
            <div className="text-amber-800 font-semibold">
              Aggiungi {missing} pz e prendi la scatola da {boxQty}!
            </div>
            <div className="text-amber-700 mt-0.5">
              Ogni pezzo ti costa € {cadBox.toFixed(2).replace('.', ',')} invece di € {unitPrice.toFixed(2).replace('.', ',')}
            </div>
            <button
              onClick={() => onSetQty(nextBoxQty)}
              className="mt-1.5 h-6 px-2 rounded text-[10px] bg-amber-600 text-white font-medium inline-flex items-center gap-1 hover:bg-amber-700 transition-colors whitespace-nowrap"
              data-testid="box-nudge-complete-btn"
            >
              Completa ({nextBoxQty} pz) <ArrowRight className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Badge compatto per liste prodotto
export function BoxBadge({ sku }: { sku: string }) {
  const box = PRODUCT_BOX_DISCOUNTS[sku];
  if (!box) return null;
  return (
    <span
      className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-0.5 whitespace-nowrap"
      data-testid="box-badge"
    >
      <Package className="w-2.5 h-2.5" /> Scatola x{box.boxQty}
    </span>
  );
}
